import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import * as endpoints from 'pkg/api/endpoints/auto';
import asyncLocalStorage from 'pkg/asyncLocalStorage';
import * as sdk from 'pkg/core/sdk';
import * as logger from 'pkg/logger';
import {
	isPushNotificationsRegistered,
	queuePushNotificationAction,
} from 'pkg/notifications';
import { setHasUnread } from 'pkg/notifications/context';
import { pushState } from 'pkg/router/state';

/**
 * @NOTE The plugin actions return promises, so they must be awaited for the
 * catch to apply. Without it a rejection escapes as an unhandled rejection,
 * which is how the iOS "capacitorDidRegisterForRemoteNotifications not called"
 * error reached Sentry.
 */
export async function callPushNotificationsAction(
	key: string,
	callable: CallableFunction
): Promise<void> {
	if (Capacitor.isPluginAvailable('PushNotifications') === false) return;

	// Registration completes asynchronously, so an action attempted during
	// app start has to wait for the device token rather than be dropped —
	// otherwise a notification read in that window stays in the tray.
	if (!isPushNotificationsRegistered()) {
		queuePushNotificationAction(key, callable);

		return;
	}

	try {
		await callable();
	} catch (error) {
		logger.error(
			error instanceof Error
				? error
				: new Error('Push notification action failed'),
			{ error }
		);
	}
}

export { setHasUnread };

export async function fetchNotifications(accountId: number): Promise<void> {
	const queryParams = {
		'only-meta': 1,
	};

	const request = await sdk.get(
		endpoints.AccountNotifications.Index(accountId),
		queryParams
	);
	if (!request.ok) {
		return;
	}

	const response = await request.json();

	setHasUnread(response.meta?.unreadNotificationsCount > 0);

	return;
}

export interface PushNotificationMetaData {
	'resource-type'?: string;
	'resource-id'?: string;
	'notification-id'?: string;
	url?: string;
}

export async function markAllAsRead(accountId: number): Promise<void> {
	const request = await sdk.post(
		endpoints.AccountNotifications.MarkAllAsRead(accountId)
	);

	if (request.ok) {
		setHasUnread(false);

		await callPushNotificationsAction('removeAllDelivered', () =>
			PushNotifications.removeAllDeliveredNotifications()
		);
	}
}

export async function markAsRead(notificationId: string): Promise<boolean> {
	const request = await sdk.patch(
		endpoints.AccountNotifications.Update(notificationId),
		{},
		{ isRead: true }
	);

	if (request.ok) {
		await callPushNotificationsAction('removeAllDelivered', () =>
			PushNotifications.removeAllDeliveredNotifications()
		);

		return true;
	}

	return false;
}

export async function deregisterPush(): Promise<void> {
	if (Capacitor.isPluginAvailable('PushNotifications') === false) {
		return;
	}

	if (isPushNotificationsRegistered()) {
		try {
			await PushNotifications.unregister();
		} catch (error) {
			// Firebase may not be initialized in development without google-services.json
			console.warn('Failed to unregister push notifications:', error);
		}
	}

	const pnsID = await asyncLocalStorage.getItem('pnsID');
	if (pnsID === null) {
		return;
	}

	asyncLocalStorage.removeItem('pnsID');

	await sdk.destroy(
		endpoints.Users.DeregisterPushNotificationDevice(Number(pnsID))
	);
}

export function navigatePushNotification(data: PushNotificationMetaData) {
	if (data?.['notification-id']) {
		markAsRead(data?.['notification-id']);
	}

	if (data?.url) {
		pushState(data.url);
	}
}
